/**
 * `<AuthProvider />` + `useAuth()` — staff session state for the
 * admin SPA. Mirrors the dashboard's `AuthProvider` but typed on
 * `StaffPrincipalDto` (the admin never sees a merchant `User`).
 *
 * Three responsibilities:
 *
 *   1. **Bootstrap auth check** on first render: GET /internal/admin/me.
 *      200 → hydrate `staff`. 401 → route to `'login'`. Other →
 *      route to `'login'` with a soft "could not reach the service"
 *      error state.
 *
 *   2. **In-flight 401 routing** — the HTTP client's `on_unauthorized`
 *      callback (wired in `providers.tsx`) clears local state +
 *      routes to login.
 *
 *   3. **Expose `staff`** via `useAuth()` for the TopBar to render
 *      the signed-in email + the Logout button.
 *
 * The bootstrap renders a loading splash before the check resolves so
 * we don't flash the console to a soon-to-be-signed-out staff member.
 */

import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import type { StaffPrincipalDto } from '@sill/types/api';

import { useAdminApi } from '../api/api-context.js';
import { ApiError } from '../api/http-client.js';
import type { SetView } from './view-router.js';

export type AuthState =
  | { kind: 'bootstrap' }
  | { kind: 'authenticated'; staff: StaffPrincipalDto }
  | { kind: 'unauthenticated' }
  | { kind: 'service_error' };

export interface AuthCtxValue {
  state: AuthState;
  /** Re-fetch /internal/admin/me; used after a successful token consume. */
  refresh: () => Promise<void>;
  /** Drop local state without calling the server. */
  clear: () => void;
  /** POST /internal/admin/auth/logout, then drop local state + route to login. */
  signOut: () => Promise<void>;
}

const AuthCtx = createContext<AuthCtxValue | null>(null);

export function useAuth(): AuthCtxValue {
  const ctx = useContext(AuthCtx);
  if (ctx === null) {
    throw new Error('useAuth() called outside <AuthProvider>');
  }
  return ctx;
}

export interface AuthProviderProps {
  children: ReactNode;
  setView: SetView;
  /** Optional initial state for tests. Production starts in `'bootstrap'`. */
  initial?: AuthState;
  /**
   * When `true` the provider does NOT run its first-mount GET /me
   * bootstrap. Used by `<App />` when the SPA starts on the
   * `'verify'` view (a magic-link callback with `?token=…` in the
   * URL): the bootstrap fires BEFORE VerifyView consumes the token,
   * gets a 401, sets state to `unauthenticated`, and routes the user
   * to login — and VerifyView's later `setView('accounts')` is then
   * dropped because the route change unmounted the view and its
   * `cancelled` flag short-circuited the async tail.
   *
   * In skip-bootstrap mode the provider leaves state at `bootstrap`
   * until VerifyView calls `refresh()` after the token consume, which
   * is the call that knows the cookie has been set. The VerifyView
   * effect drives the whole transition (consume → refresh → setView)
   * with no racing bootstrap underneath it.
   */
  skipBootstrap?: boolean;
}

export function AuthProvider({
  children,
  setView,
  initial,
  skipBootstrap,
}: AuthProviderProps): JSX.Element {
  const api = useAdminApi();
  const [state, setState] = useState<AuthState>(initial ?? { kind: 'bootstrap' });

  /**
   * Refresh the staff principal from `GET /internal/admin/me`. Two
   * call sites:
   *
   *   - Mount-time bootstrap below (only when `skipBootstrap !== true`).
   *   - `<VerifyView />` after a successful token consume.
   *
   * Bootstrap-path 401s route to `'login'` (the user has no session).
   * VerifyView-path 401s would mean "the cookie didn't take" — same
   * `setView('login')` is the right move; the verify view will have
   * already moved on by the time we get back here.
   */
  const refresh = async (): Promise<void> => {
    try {
      const res = await api.me({ throwOnUnauthorized: true });
      setState({ kind: 'authenticated', staff: res.staff });
    } catch (err) {
      if (err instanceof ApiError && err.kind === 'unauthorized') {
        setState({ kind: 'unauthenticated' });
        setView('login');
        return;
      }
      // Network / 5xx / unexpected status. The login view will read
      // the auth state and surface "Could not reach the admin service.
      // Try again in a moment."
      setState({ kind: 'service_error' });
      setView('login');
    }
  };

  const clear = (): void => {
    setState({ kind: 'unauthenticated' });
  };

  const signOut = async (): Promise<void> => {
    try {
      await api.logout();
    } catch {
      // Swallow — server-side session may already be gone. The local
      // sign-out is intent enough; the cookie will expire.
    }
    clear();
    setView('login');
  };

  useEffect(() => {
    if (state.kind !== 'bootstrap') return;
    if (skipBootstrap === true) return;
    void refresh();
    // Run-once on mount; same pattern as the dashboard's AuthProvider.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthCtx.Provider value={{ state, refresh, clear, signOut }}>
      {children}
    </AuthCtx.Provider>
  );
}
